import { useEffect, useRef } from 'react';

/* ═══ CANVAS: Organic Paint Bloom (replaces WireSphere) ═══ */
export function WireSphere() {
  const ref = useRef(null);
  const raf = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });
  const visible = useRef(true);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d");
    const resize = () => { c.width = c.offsetWidth * 2; c.height = c.offsetHeight * 2; };
    resize(); window.addEventListener("resize", resize);

    const blobs = [
      { x: 0.3, y: 0.4, r: 0.28, color: [196, 148, 74], vx: 0.7, vy: 0.5, phase: 0 },
      { x: 0.7, y: 0.3, r: 0.22, color: [139, 105, 20], vx: -0.5, vy: 0.8, phase: 1.5 },
      { x: 0.5, y: 0.65, r: 0.24, color: [232, 207, 160], vx: 0.6, vy: -0.4, phase: 3 },
      { x: 0.35, y: 0.7, r: 0.18, color: [120, 80, 40], vx: -0.8, vy: -0.3, phase: 4.5 },
      { x: 0.65, y: 0.5, r: 0.16, color: [90, 110, 160], vx: 0.3, vy: 0.7, phase: 2 },
      { x: 0.5, y: 0.35, r: 0.3, color: [196, 148, 74], vx: -0.4, vy: 0.6, phase: 5 },
      { x: 0.2, y: 0.55, r: 0.14, color: [160, 100, 60], vx: 0.5, vy: -0.6, phase: 3.8 },
    ];

    const handleMouse = (e) => { const r = c.getBoundingClientRect(); mouse.current = { x: (e.clientX - r.left) / r.width - 0.5, y: (e.clientY - r.top) / r.height - 0.5 }; };
    window.addEventListener("mousemove", handleMouse);

    let t = 0;
    const draw = () => {
      if (!visible.current) { t++; raf.current = requestAnimationFrame(draw); return; }
      const w = c.width, h = c.height;
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = "screen";

      blobs.forEach((b) => {
        const bx = (b.x + Math.sin(t * 0.003 * b.vx + b.phase) * 0.1 + mouse.current.x * 0.05) * w;
        const by = (b.y + Math.cos(t * 0.003 * b.vy + b.phase) * 0.08 + mouse.current.y * 0.04) * h;
        const pulse = 0.9 + Math.sin(t * 0.004 + b.phase) * 0.12;
        const br = b.r * Math.min(w, h) * pulse;

        const grad = ctx.createRadialGradient(bx, by, 0, bx, by, br);
        grad.addColorStop(0, `rgba(${b.color[0]},${b.color[1]},${b.color[2]},0.09)`);
        grad.addColorStop(0.4, `rgba(${b.color[0]},${b.color[1]},${b.color[2]},0.04)`);
        grad.addColorStop(1, `rgba(${b.color[0]},${b.color[1]},${b.color[2]},0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(bx, by, br, 0, Math.PI * 2);
        ctx.fill();
      });

      // subtle ring echo
      const cx = w / 2 + mouse.current.x * w * 0.05, cy = h / 2 + mouse.current.y * h * 0.04;
      const ringR = Math.min(w, h) * 0.32 + Math.sin(t * 0.003) * 20;
      ctx.globalCompositeOperation = "source-over";
      ctx.beginPath(); ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(196,148,74,${0.025 + Math.sin(t * 0.005) * 0.01})`;
      ctx.lineWidth = 1; ctx.stroke();

      t++; raf.current = requestAnimationFrame(draw);
    };

    const obs = new IntersectionObserver(([e]) => { visible.current = e.isIntersecting; }, { threshold: 0 });
    obs.observe(c);
    draw();
    return () => { cancelAnimationFrame(raf.current); window.removeEventListener("resize", resize); window.removeEventListener("mousemove", handleMouse); obs.disconnect(); };
  }, []);
  return <canvas ref={ref} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />;
}

/* ═══ Brush-stroke Canvas (About) ═══ */
export function BrushCanvas() {
  const ref = useRef(null);
  const raf = useRef(null);
  const visible = useRef(false);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d"); c.width = 600; c.height = 800;
    const strokes = Array.from({ length: 36 }, (_, i) => ({
      x: Math.random() * 600, y: Math.random() * 800,
      w: i < 3 ? 120 + Math.random() * 80 : 20 + Math.random() * 80,
      h: i < 3 ? 8 + Math.random() * 16 : 4 + Math.random() * 12,
      angle: Math.random() * Math.PI, speed: 0.002 + Math.random() * 0.004,
      hue: [36, 15, 280, 200, 340, 45][Math.floor(Math.random() * 6)],
      alpha: 0.03 + Math.random() * 0.06, phase: Math.random() * Math.PI * 2
    }));
    let t = 0;
    const draw = () => {
      if (visible.current) {
        ctx.fillStyle = "rgba(3,3,3,0.03)"; ctx.fillRect(0, 0, 600, 800);
        strokes.forEach((s) => { ctx.save(); ctx.translate(s.x + Math.sin(t * s.speed + s.phase) * 20, s.y + Math.cos(t * s.speed * 0.7 + s.phase) * 15); ctx.rotate(s.angle + Math.sin(t * s.speed * 0.5) * 0.2); ctx.fillStyle = `hsla(${s.hue},60%,45%,${s.alpha + Math.sin(t * 0.01 + s.phase) * 0.02})`; ctx.beginPath(); ctx.ellipse(0, 0, s.w / 2, s.h / 2, 0, 0, Math.PI * 2); ctx.fill(); ctx.restore(); });
        // splatters
        if (t % 90 === 0) { for (let i = 0; i < 3; i++) { ctx.beginPath(); ctx.arc(Math.random() * 600, Math.random() * 800, 1 + Math.random() * 3, 0, Math.PI * 2); ctx.fillStyle = `rgba(196,148,74,${0.04 + Math.random() * 0.04})`; ctx.fill(); } }
        t++;
      }
      raf.current = requestAnimationFrame(draw);
    };
    const obs = new IntersectionObserver(([e]) => { visible.current = e.isIntersecting; }, { threshold: 0 });
    obs.observe(c);
    draw();
    return () => { cancelAnimationFrame(raf.current); obs.disconnect(); };
  }, []);
  return <canvas ref={ref} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.6 }} />;
}

/* ═══ PaintingBreath: subtle WebGL displacement + golden tint на статичной картине.
       Stripped-down версия Lab-шейдера без crossfade/audio/RGB-split.
       На hover усиливается amplitude. Fallback — обычный <img>. ═══ */
const BREATH_VERT = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position, 1.0); }`;
const BREATH_FRAG = `
precision highp float;
uniform sampler2D uTex;
uniform float uTime;
uniform float uHover;
uniform float uIntensity;
varying vec2 vUv;

void main(){
  vec2 uv = vUv;
  vec2 d = uv - vec2(0.5);
  float dist = length(d);
  float ripple = sin(dist * 14.0 - uTime * 1.0) * 0.006 * uIntensity * (1.0 + uHover * 2.5) / (dist + 0.18);
  uv -= ripple * normalize(d + 0.0001);
  vec3 col = texture2D(uTex, uv).rgb;
  vec3 gold = vec3(0.83, 0.63, 0.29);
  col = mix(col, col * (1.0 + 0.10 * sin(uTime * 0.6)), 0.12);
  col += gold * 0.04 * (1.0 - dist) * (0.5 + uHover * 0.5);
  gl_FragColor = vec4(col, 1.0);
}
`;

export function PaintingBreath({ src, intensity = 0.3, alt = '' }) {
  const cnvRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const cnv = cnvRef.current;
    const wrap = wrapRef.current;
    if (!cnv || !wrap || !src) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let cleanup = () => {};
    let cancelled = false;
    // dynamic import — Three.js остаётся в отдельном chunk, не блоатит главный bundle
    import('three').then((THREE) => {
      if (cancelled) return;
      let renderer;
      try {
        renderer = new THREE.WebGLRenderer({ canvas: cnv, alpha: true, antialias: true });
      } catch {
        return; // нет WebGL — fallback <img> в JSX
      }
      renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 1.5));

      const scene = new THREE.Scene();
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -1, 1);
      const uniforms = {
        uTex:       { value: null },
        uTime:      { value: 0 },
        uHover:     { value: 0 },
        uIntensity: { value: intensity },
      };
      const mat = new THREE.ShaderMaterial({ uniforms, vertexShader: BREATH_VERT, fragmentShader: BREATH_FRAG });
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
      scene.add(mesh);

      const loader = new THREE.TextureLoader();
      loader.load(src, (tex) => {
        tex.minFilter = THREE.LinearFilter;
        uniforms.uTex.value = tex;
      });

      function fit() {
        const r = wrap.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) renderer.setSize(r.width, r.height, false);
      }
      fit();
      const ro = new ResizeObserver(fit);
      ro.observe(wrap);

      let visible = true;
      const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { threshold: 0 });
      io.observe(wrap);

      let hovered = false;
      const onEnter = () => { hovered = true; };
      const onLeave = () => { hovered = false; };
      wrap.addEventListener('mouseenter', onEnter);
      wrap.addEventListener('mouseleave', onLeave);

      const clock = new THREE.Clock();
      let raf;
      function loop() {
        raf = requestAnimationFrame(loop);
        if (!visible || !uniforms.uTex.value) return;
        uniforms.uTime.value += clock.getDelta();
        uniforms.uHover.value += ((hovered ? 1 : 0) - uniforms.uHover.value) * 0.06;
        renderer.render(scene, camera);
      }
      raf = requestAnimationFrame(loop);

      cleanup = () => {
        cancelAnimationFrame(raf);
        ro.disconnect();
        io.disconnect();
        wrap.removeEventListener('mouseenter', onEnter);
        wrap.removeEventListener('mouseleave', onLeave);
        mesh.geometry.dispose();
        mat.dispose();
        uniforms.uTex.value?.dispose?.();
        renderer.dispose();
      };
    }).catch(() => {});

    return () => { cancelled = true; cleanup(); };
  }, [src, intensity]);

  return (
    <div ref={wrapRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
      {/* Fallback img всегда в DOM — если WebGL не стартанул, виден он. Canvas рисует поверх когда работает. */}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
      />
      <canvas ref={cnvRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1 }} />
    </div>
  );
}
